import { Card, Skeleton, Typography, theme as antdTheme } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import { MaskedAmount, RevealGroup, RevealToggle } from '@/shared/components/masked-amount';

export type KpiTone = 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';

interface Props {
  label: ReactNode;
  value: ReactNode;
  /** Small text under the value (e.g. "out of 743 students"). */
  hint?: ReactNode;
  /** Trend chip — % vs prior period, optional. */
  trend?: { direction: 'up' | 'down'; label: string; good?: boolean };
  icon?: ReactNode;
  tone?: KpiTone;
  /** If true, value + hint render through `<MaskedAmount>` (eye toggle). */
  sensitive?: boolean;
  /** Optional sparkline / mini-chart inline at the bottom. */
  spark?: ReactNode;
  /** Click target — turns the card into a button surface. */
  onClick?: () => void;
}

/** Maps tone → semantic AntD token name. */
function toneAccent(tone: KpiTone, token: ReturnType<typeof antdTheme.useToken>['token']): string {
  switch (tone) {
    case 'success': return token.colorSuccess;
    case 'warning': return token.colorWarning;
    case 'danger':  return token.colorError;
    case 'info':    return token.colorInfo;
    case 'neutral': return token.colorTextSecondary;
    case 'primary':
    default:        return token.colorPrimary;
  }
}

/**
 * Dashboard stat card. The accent color comes from `tone` (semantic, not
 * raw hex) so all six themes restyle the badge automatically.
 *
 * Compose into a grid yourself — e.g.:
 *
 *   <Row gutter={[16,16]}>
 *     {kpis.map(k => <Col xs={24} sm={12} xl={6} key={k.key}>
 *       <KpiCard {...k} />
 *     </Col>)}
 *   </Row>
 */
export function KpiCard({
  label, value, hint, trend, icon, tone = 'primary', sensitive = false, spark, onClick,
}: Props) {
  const { token } = antdTheme.useToken();
  const accent = toneAccent(tone, token);

  const trendNode = trend && (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 12,
        marginLeft: 8,
        color: (trend.good ?? trend.direction === 'up') ? token.colorSuccess : token.colorError,
      }}
    >
      {trend.direction === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {trend.label}
    </span>
  );

  const card = (
    <Card
      className="lift-card"
      styles={{ body: { padding: 20 } }}
      style={{
        height: '100%',
        cursor: onClick ? 'pointer' : undefined,
        borderColor: token.colorBorderSecondary,
      }}
      onClick={onClick}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>{label}</Typography.Text>
        <span
          style={{
            width: 32, height: 32, borderRadius: 8,
            display: 'grid', placeItems: 'center',
            background: token.colorFillTertiary,
            color: accent,
            fontSize: sensitive ? 16 : undefined,
          }}
        >
          {sensitive ? <RevealToggle /> : icon}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', marginTop: 6, lineHeight: 1.2 }}>
        {sensitive ? (
          <span style={{ fontSize: 26, fontWeight: 600 }}>
            <MaskedAmount value={value} />
          </span>
        ) : (
          <span style={{ fontSize: 26, fontWeight: 600 }}>{value}</span>
        )}
        {trendNode}
      </div>
      {hint && (
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {sensitive ? <MaskedAmount value={hint} /> : hint}
        </Typography.Text>
      )}
      {spark && <div style={{ marginTop: 10 }}>{spark}</div>}
    </Card>
  );

  return sensitive ? <RevealGroup>{card}</RevealGroup> : card;
}

KpiCard.Skeleton = function KpiCardSkeleton() {
  return (
    <Card styles={{ body: { padding: 20 } }} style={{ height: '100%' }}>
      <Skeleton active paragraph={{ rows: 2 }} title={{ width: 90 }} />
    </Card>
  );
};
